// Plain audio player: replaces the controls of <audio> inside [data-audio-player] by play/pause, time and a progress bar.
// Without JavaScript the native controls stay. Only one recording plays at a time; volume and mute are shared by all players.
const format = (seconds) => {
    if (!Number.isFinite(seconds) || seconds < 0) {
        return '0:00';
    }
    const minutes = Math.floor(seconds / 60);
    const rest = String(Math.floor(seconds % 60)).padStart(2, '0');

    return `${minutes}:${rest}`;
};

const ICON_PLAY = '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path fill="currentColor" d="M8 5.5v13a1 1 0 0 0 1.5.86l10.5-6.5a1 1 0 0 0 0-1.72L9.5 4.64A1 1 0 0 0 8 5.5Z"/></svg>';
const ICON_PAUSE = '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path fill="currentColor" d="M8 5a1 1 0 0 0-1 1v12a1 1 0 0 0 2 0V6a1 1 0 0 0-1-1Zm8 0a1 1 0 0 0-1 1v12a1 1 0 0 0 2 0V6a1 1 0 0 0-1-1Z"/></svg>';

const ICON_VOLUME = '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path fill="currentColor" d="M11 5.2a.7.7 0 0 0-1.15-.54L6.4 7.5H4.5A1.5 1.5 0 0 0 3 9v6a1.5 1.5 0 0 0 1.5 1.5h1.9l3.45 2.84A.7.7 0 0 0 11 18.8V5.2Zm3.2 2.6a.75.75 0 0 0-1.06 1.06 4.4 4.4 0 0 1 0 6.28.75.75 0 1 0 1.06 1.06 5.9 5.9 0 0 0 0-8.4Zm2.65-2.65a.75.75 0 0 0-1.06 1.06 8.15 8.15 0 0 1 0 11.58.75.75 0 1 0 1.06 1.06 9.65 9.65 0 0 0 0-13.7Z"/></svg>';
const ICON_MUTED = '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path fill="currentColor" d="M11 5.2a.7.7 0 0 0-1.15-.54L6.4 7.5H4.5A1.5 1.5 0 0 0 3 9v6a1.5 1.5 0 0 0 1.5 1.5h1.9l3.45 2.84A.7.7 0 0 0 11 18.8V5.2Zm4.28 3.97a.75.75 0 0 0-1.06 1.06L15.99 12l-1.77 1.77a.75.75 0 1 0 1.06 1.06L17.05 13.06l1.77 1.77a.75.75 0 1 0 1.06-1.06L18.11 12l1.77-1.77a.75.75 0 0 0-1.06-1.06l-1.77 1.77-1.77-1.77Z"/></svg>';

// iOS keeps the volume under the control of the device: there the slider is left out and only the mute button stays
const volumeSettable = (() => {
    const probe = new Audio();
    probe.volume = 0.5;

    return 0.5 === probe.volume;
})();

export default function initAudioPlayer(root = document) {
    const players = [...root.querySelectorAll('[data-audio-player]')];

    players.forEach((player) => {
        const audio = player.querySelector('audio');
        if (!audio || player.classList.contains('is-enhanced')) {
            return;
        }
        audio.removeAttribute('controls');
        player.classList.add('is-enhanced');

        const { labelPlay, labelPause, labelPosition, labelMute, labelUnmute, labelVolume } = player.dataset;
        const toggle = document.createElement('button');
        toggle.type = 'button';
        toggle.className = 'audio-player__toggle';
        toggle.innerHTML = ICON_PLAY;
        toggle.setAttribute('aria-label', labelPlay);

        const time = document.createElement('span');
        time.className = 'audio-player__time';
        time.textContent = '0:00';

        const progress = document.createElement('input');
        progress.type = 'range';
        progress.className = 'audio-player__progress';
        progress.min = '0';
        progress.max = '1000';
        progress.value = '0';
        progress.step = '1';
        progress.setAttribute('aria-label', labelPosition);

        const duration = document.createElement('span');
        duration.className = 'audio-player__time';
        duration.textContent = '0:00';

        const mute = document.createElement('button');
        mute.type = 'button';
        mute.className = 'audio-player__mute';

        const volume = document.createElement('input');
        volume.type = 'range';
        volume.className = 'audio-player__volume';
        volume.min = '0';
        volume.max = '100';
        volume.step = '5';
        volume.setAttribute('aria-label', labelVolume);

        player.append(toggle, time, progress, duration, mute);
        if (volumeSettable) {
            player.append(volume);
        }

        const paintVolume = () => {
            const silent = audio.muted || 0 === audio.volume;
            mute.innerHTML = silent ? ICON_MUTED : ICON_VOLUME;
            mute.setAttribute('aria-label', silent ? labelUnmute : labelMute);
            volume.value = String(silent ? 0 : Math.round(audio.volume * 100));
            player.style.setProperty('--audio-volume', `${volume.value}%`);
        };

        const paint = () => {
            const total = audio.duration;
            const ratio = Number.isFinite(total) && total > 0 ? audio.currentTime / total : 0;
            progress.value = String(Math.round(ratio * 1000));
            player.style.setProperty('--audio-progress', `${ratio * 100}%`);
            time.textContent = format(audio.currentTime);
            duration.textContent = format(total);
        };

        toggle.addEventListener('click', () => {
            if (audio.paused) {
                audio.play().catch(() => {}); // refused by the browser (e.g. no permission): the button stays on play
            } else {
                audio.pause();
            }
        });
        audio.addEventListener('play', () => {
            players.forEach((other) => {
                const otherAudio = other.querySelector('audio');
                if (otherAudio && otherAudio !== audio) {
                    otherAudio.pause();
                }
            });
            toggle.innerHTML = ICON_PAUSE;
            toggle.setAttribute('aria-label', labelPause);
            player.classList.add('is-playing');
        });
        ['pause', 'ended'].forEach((name) => audio.addEventListener(name, () => {
            toggle.innerHTML = ICON_PLAY;
            toggle.setAttribute('aria-label', labelPlay);
            player.classList.remove('is-playing');
        }));
        ['timeupdate', 'loadedmetadata', 'durationchange', 'seeked'].forEach((name) => audio.addEventListener(name, paint));
        progress.addEventListener('input', () => {
            if (Number.isFinite(audio.duration)) {
                audio.currentTime = (progress.value / 1000) * audio.duration;
            }
        });

        mute.addEventListener('click', () => {
            if (audio.muted || 0 === audio.volume) {
                audio.muted = false;
                if (0 === audio.volume) {
                    audio.volume = 0.5;
                }
            } else {
                audio.muted = true;
            }
        });
        volume.addEventListener('input', () => {
            audio.muted = false;
            audio.volume = volume.value / 100;
        });
        audio.addEventListener('volumechange', () => {
            paintVolume();
            // all players on the page share the setting
            players.forEach((other) => {
                const otherAudio = other.querySelector('audio');
                if (otherAudio && otherAudio !== audio && (otherAudio.volume !== audio.volume || otherAudio.muted !== audio.muted)) {
                    otherAudio.volume = audio.volume;
                    otherAudio.muted = audio.muted;
                }
            });
        });

        paint();
        paintVolume();
    });
}
